// TelcoTrack — srovnání prostavěnosti a plánovaného příjmu (finance dashboard + polygon).
// Čísla jen z PPD srovnání a hotovosti stavby. Vyfakturovaný příjem v datech není.

import type { FinanceComparisonResult } from '../../core/models/object.model';
import { completionHotovostKc, formatFinanceKcShort } from './finance-profitability-chart.echarts';
import type { FinanceProfitabilityChartRow } from './finance-profitability-chart.util';

export interface FinanceProgressDonutModel {
  /** 0–100+, jedno desetinné místo. */
  percent: number;
  percentLabel: string;
  /** „X z Y“, jen když obě částky dávají smysl. */
  caption: string | null;
}

export interface FinanceProgressCategoryBar {
  id: string;
  label: string;
  /** Šířka 0–100 vůči nejdelšímu sloupci obou řad. */
  physicalWidthPercent: number;
  incomeWidthPercent: number;
  /** null = řadu vynechat (hotovost stavby není k dispozici). */
  physicalLabel: string | null;
  incomeLabel: string | null;
}

export interface FinanceProgressSummaryLine {
  id: 'budget' | 'built' | 'milestone';
  label: string;
  valueLabel: string;
  detailLabel: string | null;
}

export interface FinanceProgressNote {
  text: string;
  tone: 'green' | 'warning';
}

export interface FinanceProgressComparisonModel {
  physical: FinanceProgressDonutModel | null;
  income: FinanceProgressDonutModel | null;
  note: FinanceProgressNote | null;
  categories: FinanceProgressCategoryBar[];
  summary: FinanceProgressSummaryLine[];
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function finiteNumber(value: number | null | undefined): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatFinanceProgressPercent(value: number): string {
  const rounded = round1(value);
  const text = new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(rounded);
  return `${text} %`;
}

function formatGapPoints(gap: number): string {
  const abs = Math.abs(round1(gap));
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: Number.isInteger(abs) ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(abs);
}

/** Věta jen z rozdílu zobrazených procent. Kladný rozdíl = příjmy jsou napřed. */
export function financeCoverageNote(physicalPercent: number, coveragePercent: number): FinanceProgressNote {
  const gap = round1(round1(coveragePercent) - round1(physicalPercent));
  if (gap <= -1) {
    return {
      tone: 'warning',
      text: `Finanční krytí zaostává za prostavěností (−${formatGapPoints(gap)} % v příjmech)`,
    };
  }
  if (gap >= 1) {
    return {
      tone: 'green',
      text: `Finanční krytí odpovídá prostavěnosti (+${formatGapPoints(gap)} % předstih v příjmech)`,
    };
  }
  return { tone: 'green', text: 'Finanční krytí odpovídá prostavěnosti' };
}

function sharePercent(part: number, total: number): number {
  if (total <= 0) return 0;
  return round1((part / total) * 100);
}

/** Rozdělí částku podle vah a dorovná zaokrouhlení, aby součet seděl na celek. */
function allocateByWeight(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((acc, n) => acc + n, 0);
  if (sum <= 0) return weights.map(() => 0);
  const rounded = weights.map((weight) => Math.round((total * weight) / sum));
  const drift = Math.round(total) - rounded.reduce((acc, n) => acc + n, 0);
  if (drift !== 0) {
    let index = 0;
    weights.forEach((weight, i) => {
      if (weight > weights[index]) index = i;
    });
    rounded[index] += drift;
  }
  return rounded;
}

function moneyLabel(amount: number, share: number): string {
  return `${formatFinanceKcShort(amount)} (${formatFinanceProgressPercent(share)})`;
}

function categoryRows(rows: readonly FinanceProfitabilityChartRow[]): FinanceProfitabilityChartRow[] {
  return rows.filter((row) => row.id !== 'celkem');
}

/**
 * Srovnání fyzického pokroku a milníkového příjmu.
 *
 * - Rozpočet (výdej) = `plannedTotal` (PPD práce + materiál).
 * - Prostavěnost % = vážená hotovost stavby (`celkem.completionPercent`).
 * - Prostavěno Kč = stejné procento z rozpočtu (`completionHotovostKc`).
 * - Sloupec kategorie (fyzický) = ten součet rozdělený podle hotovosti plánu práce.
 * - Plánovaný příjem (milník) = součet plánu práce kategorií × jejich hotovost.
 * - Finanční krytí % = milníkový příjem / součet plánovaného příjmu kategorií.
 * - Sloupec kategorie (peníze) = plánovaný příjem práce kategorie a jeho podíl.
 * - Skutečně vyfakturováno se nepočítá — finance API ho neposílá.
 */
export function buildFinanceProgressComparison(args: {
  comparison: FinanceComparisonResult;
  rows: readonly FinanceProfitabilityChartRow[];
  progressKnown: boolean;
}): FinanceProgressComparisonModel {
  const categories = categoryRows(args.rows);
  const totalRow = args.rows.find((row) => row.id === 'celkem');
  const physicalRaw = args.progressKnown ? finiteNumber(totalRow?.completionPercent) : null;
  const physicalPercent = physicalRaw == null ? null : Math.max(0, round1(physicalRaw));

  const budget = finiteNumber(args.comparison.plannedTotal);
  const builtKc =
    physicalPercent != null && budget != null && budget > 0
      ? completionHotovostKc(budget, physicalPercent)
      : null;

  const categoryPlanned = categories.reduce((sum, row) => sum + Math.max(0, Number(row.plannedIncome) || 0), 0);
  const categoryMilestone = args.progressKnown
    ? categories.reduce(
        (sum, row) =>
          sum + completionHotovostKc(Math.max(0, Number(row.plannedIncome) || 0), Math.max(0, Number(row.completionPercent) || 0)),
        0,
      )
    : null;
  const incomeBase = categoryPlanned > 0 ? categoryPlanned : null;
  const coveragePercent =
    categoryMilestone != null && incomeBase != null && incomeBase > 0
      ? round1((categoryMilestone / incomeBase) * 100)
      : null;

  const hotovostWeights = categories.map((row) =>
    args.progressKnown
      ? completionHotovostKc(Math.max(0, Number(row.plannedIncome) || 0), Math.max(0, Number(row.completionPercent) || 0))
      : 0,
  );
  const weightSum = hotovostWeights.reduce((sum, n) => sum + n, 0);
  const canSplitPhysical = args.progressKnown && weightSum > 0;
  const physicalAmounts = canSplitPhysical
    ? allocateByWeight(builtKc != null ? builtKc : weightSum, hotovostWeights)
    : hotovostWeights.map(() => 0);
  const incomeAmounts = categories.map((row) => Math.max(0, Number(row.plannedIncome) || 0));
  const physicalTotal = physicalAmounts.reduce((sum, n) => sum + n, 0);
  const incomeTotal = incomeAmounts.reduce((sum, n) => sum + n, 0);
  const widthMax = Math.max(0, ...physicalAmounts, ...incomeAmounts);

  const summary: FinanceProgressSummaryLine[] = [];
  if (budget != null) {
    summary.push({
      id: 'budget',
      label: 'Celkový rozpočet (Výdej)',
      valueLabel: formatFinanceKcShort(budget),
      detailLabel: null,
    });
  }
  if (builtKc != null && physicalPercent != null) {
    summary.push({
      id: 'built',
      label: 'Dosud prostavěno',
      valueLabel: formatFinanceKcShort(builtKc),
      detailLabel: `(${formatFinanceProgressPercent(physicalPercent)} z celku)`,
    });
  }
  if (categoryMilestone != null && incomeBase != null) {
    summary.push({
      id: 'milestone',
      label: 'Plánovaný příjem (Milník)',
      valueLabel: formatFinanceKcShort(categoryMilestone),
      detailLabel: null,
    });
  }

  return {
    physical:
      physicalPercent == null
        ? null
        : {
            percent: physicalPercent,
            percentLabel: formatFinanceProgressPercent(physicalPercent),
            caption:
              builtKc != null && budget != null && budget > 0
                ? `Prostavěno: ${formatFinanceKcShort(builtKc)} z ${formatFinanceKcShort(budget)}`
                : null,
          },
    income:
      coveragePercent == null || categoryMilestone == null || incomeBase == null
        ? null
        : {
            percent: coveragePercent,
            percentLabel: formatFinanceProgressPercent(coveragePercent),
            caption: `Plánováno dle milníku: ${formatFinanceKcShort(categoryMilestone)} z ${formatFinanceKcShort(incomeBase)}`,
          },
    note:
      physicalPercent != null && coveragePercent != null
        ? financeCoverageNote(physicalPercent, coveragePercent)
        : null,
    categories: categories.map((row, index) => {
      const physicalKc = physicalAmounts[index] ?? 0;
      const incomeKc = incomeAmounts[index] ?? 0;
      return {
        id: row.id,
        label: row.label,
        physicalWidthPercent: widthMax > 0 ? (physicalKc / widthMax) * 100 : 0,
        incomeWidthPercent: widthMax > 0 ? (incomeKc / widthMax) * 100 : 0,
        physicalLabel: canSplitPhysical ? moneyLabel(physicalKc, sharePercent(physicalKc, physicalTotal)) : null,
        incomeLabel: moneyLabel(incomeKc, sharePercent(incomeKc, incomeTotal)),
      };
    }),
    summary,
  };
}
